import { useState, useEffect } from "react";
import { useAuth } from "./hooks/useAuth";
import { getEmpresas, addEmpresa, getContratos, addContrato } from "./services/firestore";
import { exportRankingVAF, exportContratos, importSEF } from "./utils/exportExcel";
import { gerarRelatorioVAF, gerarRelatorioIBS } from "./utils/exportPDF";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --navy: #0B1F3A; --navy-mid: #122847; --blue: #1A6FC4; --blue-bright: #2484E4;
    --cyan: #00C2D4; --gold: #F5A623; --red: #E63946; --green: #2DC653;
    --surface: #F0F4FA; --surface2: #E2EAF5; --white: #FFFFFF;
    --border: rgba(26,111,196,0.15); --text: #0B1F3A; --text-muted: #5A728A;
  }
  body { font-family: 'Sora', sans-serif; background: var(--surface); color: var(--text); margin: 0; }
  .app { display: flex; flex-direction: column; height: 100vh; overflow: hidden; }
  .header { height: 60px; background: var(--navy); display: flex; align-items: center; justify-content: space-between; padding: 0 20px; flex-shrink: 0; box-shadow: 0 2px 20px rgba(0,0,0,0.3); }
  .logo-mark { width: 34px; height: 34px; background: linear-gradient(135deg,#2484E4,#00C2D4); border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 800; color: white; flex-shrink: 0; }
  .logo-text { font-size: 14px; font-weight: 700; color: white; }
  .logo-sub { font-size: 9px; color: rgba(255,255,255,0.4); letter-spacing: 1.2px; text-transform: uppercase; }
  .alert-pill { background: rgba(230,57,70,0.18); border: 1px solid rgba(230,57,70,0.6); color: #ff7b85; font-size: 11px; padding: 4px 12px; border-radius: 20px; cursor: pointer; animation: pb 2s infinite; }
  @keyframes pb { 0%,100%{border-color:rgba(230,57,70,0.4)} 50%{border-color:rgba(230,57,70,1)} }
  .user-chip { display: flex; align-items: center; gap: 8px; padding: 4px 10px; border-radius: 7px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.1); cursor: pointer; }
  .user-avatar { width: 26px; height: 26px; background: linear-gradient(135deg,var(--blue),var(--cyan)); border-radius: 50%; font-size: 10px; font-weight: 700; color: white; display: flex; align-items: center; justify-content: center; }
  .app-body { display: flex; flex: 1; overflow: hidden; }
  .sidebar { width: 220px; background: var(--navy-mid); display: flex; flex-direction: column; overflow-y: auto; border-right: 1px solid rgba(255,255,255,0.05); flex-shrink: 0; }
  .sidebar::-webkit-scrollbar { width: 3px; } .sidebar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); }
  .nav-sec-label { font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: rgba(255,255,255,0.25); padding: 14px 18px 6px; }
  .nav-item { display: flex; align-items: center; gap: 9px; padding: 9px 18px; cursor: pointer; border-left: 3px solid transparent; color: rgba(255,255,255,0.5); font-size: 12.5px; font-weight: 500; transition: all 0.15s; user-select: none; }
  .nav-item:hover { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.85); }
  .nav-item.active { background: rgba(36,132,228,0.12); border-left-color: #2484E4; color: white; }
  .nav-badge { margin-left: auto; background: var(--red); color: white; font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 10px; }
  .nav-div { height: 1px; background: rgba(255,255,255,0.05); margin: 6px 0; }
  .main { flex: 1; overflow-y: auto; padding: 24px; }
  .main::-webkit-scrollbar { width: 5px; } .main::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 3px; }
  .page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 18px; flex-wrap: wrap; gap: 10px; }
  .page-title { font-size: 20px; font-weight: 700; color: var(--navy); letter-spacing: -0.5px; }
  .page-sub { font-size: 12.5px; color: var(--text-muted); margin-top: 2px; }
  .page-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 8px; font-family: 'Sora',sans-serif; font-size: 12.5px; font-weight: 600; cursor: pointer; transition: all 0.2s; border: none; }
  .btn-sm { padding: 6px 11px; font-size: 11.5px; }
  .btn-primary { background: var(--blue); color: white; box-shadow: 0 3px 12px rgba(26,111,196,0.3); }
  .btn-primary:hover { background: var(--blue-bright); transform: translateY(-1px); }
  .btn-outline { background: white; color: var(--navy); border: 1.5px solid var(--border); }
  .btn-outline:hover { border-color: var(--blue); color: var(--blue); }
  .btn-ghost { background: transparent; color: var(--text-muted); border: 1.5px solid var(--border); }
  .btn-ghost:hover { color: var(--navy); border-color: var(--navy); }
  .cards-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; margin-bottom: 20px; }
  .kpi-card { background: white; border-radius: 12px; padding: 18px 20px; border: 1px solid var(--border); position: relative; overflow: hidden; cursor: default; transition: transform 0.2s, box-shadow 0.2s; animation: fu 0.4s ease both; }
  .kpi-card:hover { transform: translateY(-2px); box-shadow: 0 6px 24px rgba(0,0,0,0.08); }
  @keyframes fu { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  .card-accent { position: absolute; top: 0; left: 0; width: 4px; height: 100%; border-radius: 12px 0 0 12px; }
  .card-label { font-size: 10.5px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; }
  .card-value { font-size: 24px; font-weight: 700; color: var(--navy); letter-spacing: -1px; line-height: 1; font-family: 'IBM Plex Mono',monospace; }
  .card-sub { font-size: 11px; color: var(--text-muted); margin-top: 5px; display: flex; align-items: center; gap: 5px; }
  .card-icon { position: absolute; right: 16px; top: 16px; font-size: 28px; opacity: 0.09; }
  .pb { height: 4px; background: var(--surface2); border-radius: 10px; overflow: hidden; margin-top: 7px; }
  .pf { height: 100%; border-radius: 10px; }
  .trend { font-size: 10.5px; font-weight: 700; padding: 2px 5px; border-radius: 5px; }
  .tu { background: rgba(45,198,83,0.12); color: #1e9042; }
  .td { background: rgba(230,57,70,0.12); color: var(--red); }
  .tw { background: rgba(245,166,35,0.12); color: #c08a00; }
  .alert-strip { background: rgba(230,57,70,0.04); border: 1px solid rgba(230,57,70,0.2); border-radius: 9px; padding: 11px 14px; display: flex; align-items: center; gap: 10px; margin-bottom: 18px; }
  .alert-action { font-size: 12px; font-weight: 700; color: var(--blue); cursor: pointer; padding: 4px 11px; border: 1.5px solid var(--blue); border-radius: 6px; transition: all 0.2s; white-space: nowrap; }
  .alert-action:hover { background: var(--blue); color: white; }
  .tab-nav { display: flex; gap: 2px; background: var(--surface2); padding: 3px; border-radius: 9px; width: fit-content; margin-bottom: 20px; flex-wrap: wrap; }
  .tab-btn { padding: 6px 14px; border-radius: 7px; font-size: 12px; font-weight: 600; cursor: pointer; border: none; background: transparent; color: var(--text-muted); font-family: 'Sora',sans-serif; transition: all 0.2s; }
  .tab-btn.active { background: white; color: var(--navy); box-shadow: 0 2px 6px rgba(0,0,0,0.1); }
  .charts-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 14px; margin-bottom: 20px; }
  .chart-card { background: white; border-radius: 12px; padding: 18px; border: 1px solid var(--border); }
  .chart-title { font-size: 12.5px; font-weight: 700; color: var(--navy); margin-bottom: 14px; display: flex; align-items: center; justify-content: space-between; }
  .legend-item { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--text-muted); }
  .legend-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .legend-val { font-weight: 700; color: var(--navy); font-family: 'IBM Plex Mono',monospace; margin-left: auto; }
  .table-card { background: white; border-radius: 12px; border: 1px solid var(--border); overflow: hidden; margin-bottom: 20px; animation: fu 0.4s ease both; }
  .table-header { padding: 14px 18px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid var(--border); flex-wrap: wrap; gap: 8px; }
  .table-title { font-size: 13px; font-weight: 700; color: var(--navy); }
  .tw-wrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; padding: 10px 14px; font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); background: var(--surface); border-bottom: 1px solid var(--border); white-space: nowrap; }
  td { padding: 11px 14px; font-size: 12px; border-bottom: 1px solid rgba(0,0,0,0.04); color: var(--text); }
  tr:last-child td { border-bottom: none; }
  tr:hover td { background: var(--surface); }
  .mono { font-family: 'IBM Plex Mono',monospace; }
  .bold { font-weight: 600; }
  .badge { display: inline-flex; align-items: center; gap: 3px; padding: 3px 8px; border-radius: 20px; font-size: 10.5px; font-weight: 600; white-space: nowrap; }
  .badge-red { background: rgba(230,57,70,0.1); color: var(--red); }
  .badge-yellow { background: rgba(245,166,35,0.12); color: #c08a00; }
  .badge-green { background: rgba(45,198,83,0.1); color: #1e9042; }
  .badge-blue { background: rgba(26,111,196,0.1); color: var(--blue); }
  .badge-gray { background: rgba(0,0,0,0.06); color: var(--text-muted); }
  .form-card { background: white; border-radius: 12px; border: 1px solid var(--border); padding: 22px; margin-bottom: 18px; animation: fu 0.3s ease both; }
  .form-title { font-size: 14px; font-weight: 700; color: var(--navy); margin-bottom: 18px; padding-bottom: 12px; border-bottom: 1px solid var(--border); }
  .fg { display: grid; grid-template-columns: repeat(auto-fill,minmax(220px,1fr)); gap: 14px; }
  .fg-full { grid-column: 1/-1; }
  .fgi { display: flex; flex-direction: column; gap: 5px; }
  label { font-size: 11px; font-weight: 600; color: var(--text-muted); }
  .req::after { content:" *"; color:var(--red); }
  input,select,textarea { padding: 9px 12px; border-radius: 8px; border: 1.5px solid var(--border); font-family: 'Sora',sans-serif; font-size: 12.5px; color: var(--text); background: var(--surface); transition: border-color 0.2s,box-shadow 0.2s; outline: none; width: 100%; }
  input:focus,select:focus,textarea:focus { border-color: var(--blue); box-shadow: 0 0 0 3px rgba(26,111,196,0.1); background: white; }
  textarea { resize: vertical; min-height: 80px; }
  .form-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--border); }
  .igroup { position: relative; }
  .iprefix { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); font-size: 12px; color: var(--text-muted); pointer-events: none; }
  .igroup input { padding-left: 28px; }
  .drop-zone { border: 2px dashed var(--border); border-radius: 12px; padding: 32px 20px; text-align: center; cursor: pointer; transition: all 0.2s; background: var(--surface); }
  .drop-zone:hover { border-color: var(--blue); background: rgba(26,111,196,0.04); }
  .map-container { background: var(--navy-mid); border-radius: 12px; overflow: hidden; position: relative; min-height: 380px; }
  .map-filter-btn { padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; cursor: pointer; border: 1.5px solid rgba(255,255,255,0.2); background: rgba(0,0,0,0.3); color: rgba(255,255,255,0.7); transition: all 0.2s; font-family:'Sora',sans-serif; }
  .map-filter-btn.active { background: var(--blue); border-color: var(--blue); color: white; }
  .map-pin { position: absolute; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; cursor: pointer; transition: transform 0.2s; box-shadow: 0 2px 8px rgba(0,0,0,0.4); }
  .map-pin:hover { transform: scale(1.6); }
  .toast { position: fixed; bottom: 20px; right: 20px; padding: 12px 18px; border-radius: 10px; font-size: 13px; font-weight: 600; color: white; z-index: 9999; animation: su 0.3s ease; box-shadow: 0 6px 24px rgba(0,0,0,0.2); font-family:'Sora',sans-serif; }
  .toast-success { background: var(--green); }
  .toast-error { background: var(--red); }
  @keyframes su { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  .login-screen { height: 100vh; background: var(--navy); display: flex; align-items: center; justify-content: center; }
  .login-card { background: white; border-radius: 18px; padding: 36px; width: 360px; box-shadow: 0 20px 60px rgba(0,0,0,0.4); }
  .login-btn { width: 100%; padding: 11px; background: var(--blue); color: white; border: none; border-radius: 9px; font-family:'Sora',sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.2s; margin-top: 6px; }
  .login-btn:hover { background: var(--blue-bright); transform: translateY(-1px); }
  .user-row { display: flex; align-items: center; gap: 12px; padding: 11px 14px; border-bottom: 1px solid var(--border); }
  .user-row:last-child { border-bottom: none; }
  .ura { width: 34px; height: 34px; border-radius: 50%; background: linear-gradient(135deg,var(--blue),var(--cyan)); display: flex; align-items: center; justify-content: center; color: white; font-weight: 700; font-size: 12px; flex-shrink: 0; }
  @media(max-width:1000px){.cards-grid{grid-template-columns:1fr 1fr}.charts-grid{grid-template-columns:1fr}}
  @media(max-width:700px){.sidebar{display:none}.cards-grid{grid-template-columns:1fr 1fr}}
  @media(max-width:480px){.cards-grid{grid-template-columns:1fr}}
`;

const EMPRESAS = [
  {id:1,cnpj:"00.000.001/0001-00",nome:"Metalúrgica ABC Ltda",vafAnt:1240000,vafAt:612000,variacao:-50.6,perda:628000,status:"critico",prazo:32},
  {id:2,cnpj:"00.000.002/0001-00",nome:"Distribuidora XYZ S.A.",vafAnt:980000,vafAt:540000,variacao:-44.9,perda:440000,status:"critico",prazo:38},
  {id:3,cnpj:"00.000.003/0001-00",nome:"Comércio Regional ME",vafAnt:870000,vafAt:510000,variacao:-41.4,perda:360000,status:"atencao",prazo:55},
  {id:4,cnpj:"00.000.004/0001-00",nome:"Transportes Delta Ltda",vafAnt:760000,vafAt:488000,variacao:-35.8,perda:272000,status:"atencao",prazo:68},
  {id:5,cnpj:"00.000.005/0001-00",nome:"Indústria Nova Eireli",vafAnt:690000,vafAt:480000,variacao:-30.4,perda:210000,status:"monitorar",prazo:90},
  {id:6,cnpj:"00.000.006/0001-00",nome:"Agropecuária Sul Ltda",vafAnt:540000,vafAt:390000,variacao:-27.8,perda:150000,status:"monitorar",prazo:102},
];

const CONTRATOS = [
  {id:1,numero:"CT-2024-001",fornecedor:"TechService Ltda",valor:380000,secretaria:"Saúde",municipio:"Exemplo-MG",status:"ativo",ibs:31920},
  {id:2,numero:"CT-2024-002",fornecedor:"Construções Beta S.A.",valor:920000,secretaria:"Obras",municipio:"Exemplo-MG",status:"ativo",ibs:77280},
  {id:3,numero:"CT-2024-003",fornecedor:"EduTech ME",valor:240000,secretaria:"Educação",municipio:"Exemplo-MG",status:"pendente",ibs:20160},
  {id:4,numero:"CT-2024-004",fornecedor:"Saúde Integral Ltda",valor:680000,secretaria:"Saúde",municipio:"Exemplo-MG",status:"ativo",ibs:57120},
  {id:5,numero:"CT-2024-005",fornecedor:"Limpeza Total Eireli",valor:160000,secretaria:"Meio Ambiente",municipio:"Exemplo-MG",status:"sem-destino",ibs:0},
];

const fmt = v => "R$ " + (v||0).toLocaleString("pt-BR",{minimumFractionDigits:0,maximumFractionDigits:0});
const fmtK = v => v>=1e6?`R$ ${(v/1e6).toFixed(2)}M`:v>=1000?`R$ ${(v/1000).toFixed(0)}K`:fmt(v);
const fmtP = v => (v>0?"+":"")+v.toFixed(1)+"%";

function Badge({s}){
  const m={critico:["badge-red","🔴 Crítico"],atencao:["badge-yellow","⚠ Atenção"],monitorar:["badge-green","✓ Monitorar"],ativo:["badge-green","● Ativo"],pendente:["badge-yellow","⏳ Pendente"],"sem-destino":["badge-gray","— Sem Destino"]};
  const [c,t]=m[s]||["badge-gray",s];
  return <span className={`badge ${c}`}>{t}</span>;
}

function Toast({msg,type,onClose}){
  useEffect(()=>{const t=setTimeout(onClose,2800);return()=>clearTimeout(t);},[onClose]);
  return <div className={`toast toast-${type}`}>{msg}</div>;
}

// ── LOGIN ────────────────────────────────────────────────────────────────────
function Login({login,showToast}){
  const [email,setEmail]=useState(""); const [pw,setPw]=useState(""); const [loading,setLoading]=useState(false);
  const go=async()=>{
    setLoading(true);
    try {
      await login(email, pw);
    } catch (err) {
      setLoading(false);
      showToast(err.code==="auth/invalid-credential"
        ? "E-mail ou senha incorretos" : "Erro: "+err.message, "error");
    }
  };
  return(
    <div className="login-screen">
      <div className="login-card">
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:28}}>
          <div className="logo-mark">GV</div>
          <div><div className="logo-text">Gerenciador VAF IBS</div><div className="logo-sub">Gestão VAF · IBS · ICMS</div></div>
        </div>
        <div style={{fontSize:20,fontWeight:700,color:"var(--navy)",marginBottom:4}}>Bem-vindo</div>
        <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:24}}>Acesse com suas credenciais institucionais</div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div className="fgi"><label>E-mail</label><input type="email" placeholder="seu@municipio.gov.br" value={email} onChange={e=>setEmail(e.target.value)}/></div>
          <div className="fgi"><label>Senha</label><input type="password" placeholder="••••••••" value={pw} onChange={e=>setPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&go()}/></div>
          <button className="login-btn" onClick={go} disabled={loading}>{loading?"Autenticando...":"Entrar →"}</button>
        </div>
        <div style={{marginTop:18,fontSize:11,color:"var(--text-muted)",textAlign:"center"}}>🔒 Acesso restrito a servidores autorizados<br/><span style={{opacity:.55,fontSize:10}}>Demo: clique Entrar sem preencher</span></div>
      </div>
    </div>
  );
}

// ── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({setPage,empresas,contratos}){
  const totalPerda=empresas.reduce((s,e)=>s+e.perda,0);
  const totalIbs=contratos.reduce((s,c)=>s+(c.ibs||0),0);
  const criticas=empresas.filter(e=>e.status==="critico").length;
  const semDest=contratos.filter(c=>c.status==="sem-destino").length;
  const menorPrazo=empresas.filter(e=>e.status==="critico").sort((a,b)=>a.prazo-b.prazo)[0]?.prazo||0;
  const [tab,setTab]=useState("geral");

  const kpis=[
    {label:"Receita Protegida ICMS",value:fmtK(8420000),sub:"base: repasse ICMS 2024",trend:"tu",trendTxt:"▲ 4,1%",accent:"var(--green)",icon:"🛡",prog:78,progBg:"linear-gradient(90deg,#2DC653,#7be495)"},
    {label:"Perda Potencial VAF",value:fmtK(totalPerda),sub:`${empresas.length} empresas em queda`,trend:"td",trendTxt:`▼ ${empresas.length} emp.`,accent:"var(--red)",icon:"📉",prog:38,progBg:"linear-gradient(90deg,var(--red),#ff8a92)",vColor:"var(--red)"},
    {label:"IBS Estimado (2027)",value:fmtK(totalIbs),sub:"base: contratos 2024",trend:"tu",trendTxt:"▲ simulação",accent:"var(--cyan)",icon:"🔮",prog:62,vColor:"var(--blue)"},
    {label:"Empresas Críticas",value:criticas,sub:"prazo < 60 dias",trend:"td",trendTxt:`${criticas} urgentes`,accent:"var(--red)",icon:"🏭",vColor:"var(--red)"},
    {label:"Contratos Sem Destino IBS",value:semDest,sub:"volume não vinculado",trend:"tw",trendTxt:"R$ 640K",accent:"var(--gold)",icon:"📋",vColor:"var(--gold)"},
    {label:"Prazo de Contestação",value:`${menorPrazo} dias`,sub:`${criticas} pendentes`,trend:"td",trendTxt:"URGENTE",accent:"var(--red)",icon:"⏱",prog:25,progBg:"linear-gradient(90deg,var(--red),var(--gold))",vColor:"var(--red)"},
  ];

  return(
    <div>
      <div className="page-header">
        <div><div className="page-title">Dashboard de Proteção de Receita</div><div className="page-sub">Visão consolidada · Competência: Jan–Dez 2024</div></div>
        <div className="page-actions">
          <button className="btn btn-outline" onClick={()=>setPage("importacao")}>📥 Importar SEF</button>
          <button className="btn btn-primary" onClick={()=>setPage("relatorios")}>📄 Gerar Relatório</button>
        </div>
      </div>

      <div className="alert-strip">
        <span style={{fontSize:18}}>🔴</span>
        <div style={{flex:1,fontSize:12.5}}><strong style={{color:"var(--red)"}}>Prazo crítico:</strong> {menorPrazo} dias para contestação de {criticas} empresas com queda VAF acima de 40%.</div>
        <div className="alert-action" onClick={()=>setPage("vaf")}>Ver Empresas →</div>
      </div>

      <div className="tab-nav">
        {[["geral","Visão Geral"],["vaf","VAF / ICMS"],["ibs","IBS"],["territorial","Territorial"]].map(([k,l])=>(
          <button key={k} className={`tab-btn ${tab===k?"active":""}`} onClick={()=>setTab(k)}>{l}</button>
        ))}
      </div>

      {tab==="geral"&&(<>
        <div className="cards-grid">
          {kpis.map((k,i)=>(
            <div className="kpi-card" key={i} style={{animationDelay:`${i*.06}s`}}>
              <div className="card-accent" style={{background:k.accent}}/>
              <div className="card-icon">{k.icon}</div>
              <div className="card-label">{k.label}</div>
              <div className="card-value" style={{color:k.vColor||"var(--navy)"}}>{k.value}</div>
              <div className="card-sub"><span className={`trend ${k.trend}`}>{k.trendTxt}</span>{k.sub}</div>
              {k.prog!==undefined&&<div className="pb"><div className="pf" style={{width:`${k.prog}%`,background:k.progBg||"linear-gradient(90deg,var(--blue),var(--cyan))"}}/></div>}
            </div>
          ))}
        </div>

        <div className="charts-grid">
          <div className="chart-card">
            <div className="chart-title">Evolução VAF Mensal – 2024 <span style={{fontSize:10.5,fontWeight:400,color:"var(--text-muted)"}}>Comparativo 2023 vs 2024</span></div>
            <svg width="100%" height="120" viewBox="0 0 600 120" preserveAspectRatio="none">
              <line x1="0" y1="30" x2="600" y2="30" stroke="#e8eef5" strokeWidth="1"/>
              <line x1="0" y1="60" x2="600" y2="60" stroke="#e8eef5" strokeWidth="1"/>
              <line x1="0" y1="90" x2="600" y2="90" stroke="#e8eef5" strokeWidth="1"/>
              <path d="M0,80 L100,70 L200,65 L300,60 L400,55 L500,52 L600,50 L600,120 L0,120Z" fill="rgba(26,111,196,0.07)"/>
              <path d="M0,80 L100,70 L200,65 L300,60 L400,55 L500,52 L600,50" fill="none" stroke="rgba(26,111,196,0.35)" strokeWidth="1.5" strokeDasharray="5,3"/>
              <path d="M0,75 L100,62 L200,50 L300,42 L400,35 L450,43 L500,30 L600,22 L600,120 L0,120Z" fill="rgba(0,194,212,0.08)"/>
              <path d="M0,75 L100,62 L200,50 L300,42 L400,35 L450,43 L500,30 L600,22" fill="none" stroke="#00C2D4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="450" cy="43" r="5" fill="var(--gold)" stroke="white" strokeWidth="2"/>
              <circle cx="600" cy="22" r="4" fill="#00C2D4"/>
            </svg>
            <div style={{display:"flex",gap:16,marginTop:6}}>
              <div className="legend-item"><div className="legend-dot" style={{background:"#00C2D4"}}/>2024</div>
              <div className="legend-item"><div className="legend-dot" style={{background:"rgba(26,111,196,0.35)"}}/>2023</div>
              <div className="legend-item"><div className="legend-dot" style={{background:"var(--gold)"}}/>Queda detectada</div>
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-title">IBS por Secretaria</div>
            <div style={{display:"flex",alignItems:"center",gap:14}}>
              <svg width="90" height="90" viewBox="0 0 100 100" style={{flexShrink:0}}>
                <circle cx="50" cy="50" r="38" fill="none" stroke="#e8eef5" strokeWidth="14"/>
                <circle cx="50" cy="50" r="38" fill="none" stroke="var(--blue)" strokeWidth="14" strokeDasharray="95 144" strokeDashoffset="-35" strokeLinecap="round"/>
                <circle cx="50" cy="50" r="38" fill="none" stroke="var(--cyan)" strokeWidth="14" strokeDasharray="55 144" strokeDashoffset="-130" strokeLinecap="round"/>
                <circle cx="50" cy="50" r="38" fill="none" stroke="var(--gold)" strokeWidth="14" strokeDasharray="30 144" strokeDashoffset="-185" strokeLinecap="round"/>
                <text x="50" y="54" textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--navy)" fontFamily="IBM Plex Mono">67%</text>
              </svg>
              <div style={{display:"flex",flexDirection:"column",gap:7,flex:1}}>
                {[["var(--blue)","Saúde","38%"],["var(--cyan)","Obras","29%"],["var(--gold)","Educação","21%"],["#e8eef5","Outros","12%"]].map(([c,l,v])=>(
                  <div className="legend-item" key={l}><div className="legend-dot" style={{background:c,border:c==="#e8eef5"?"1px solid #ccc":"none"}}/>{l}<span className="legend-val">{v}</span></div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="table-card">
          <div className="table-header">
            <div className="table-title">🏆 Ranking de Perdas VAF – Top Empresas</div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn btn-outline btn-sm" onClick={()=>exportRankingVAF(empresas)}>📊 Exportar</button>
              <button className="btn btn-primary btn-sm" onClick={()=>setPage("vaf")}>⚡ Contestação</button>
            </div>
          </div>
          <div className="tw-wrap">
            <table>
              <thead><tr><th>#</th><th>Empresa</th><th>VAF 2023</th><th>VAF 2024</th><th>Variação</th><th>Perda</th><th>Status</th><th>Prazo</th></tr></thead>
              <tbody>
                {[...empresas].sort((a,b)=>b.perda-a.perda).map((e,i)=>(
                  <tr key={e.id}>
                    <td><strong style={{color:i<2?"var(--red)":"var(--gold)"}}>{i+1}º</strong></td>
                    <td><div className="bold">{e.nome}</div><div className="mono" style={{fontSize:10.5,color:"var(--text-muted)"}}>{e.cnpj}</div></td>
                    <td className="mono">{fmt(e.vafAnt)}</td>
                    <td className="mono">{fmt(e.vafAt)}</td>
                    <td><span className={`trend ${e.variacao<0?"td":"tu"}`}>{fmtP(e.variacao)}</span></td>
                    <td className="mono bold" style={{color:e.perda>300000?"var(--red)":"var(--gold)"}}>{fmt(e.perda)}</td>
                    <td><Badge s={e.status}/></td>
                    <td style={{fontWeight:e.prazo<45?700:400,color:e.prazo<45?"var(--red)":"var(--text-muted)"}}>{e.prazo}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>)}

      {tab==="vaf"&&(
        <div>
          <div className="cards-grid" style={{gridTemplateColumns:"repeat(3,1fr)"}}>
            {[
              {label:"Total VAF 2024",value:fmtK(empresas.reduce((s,e)=>s+e.vafAt,0)),accent:"var(--blue)",icon:"📊",sub:"soma de todas as empresas",trend:"tw",trendTxt:"Ano corrente"},
              {label:"Perda Total Potencial",value:fmtK(totalPerda),accent:"var(--red)",icon:"📉",sub:`${empresas.length} empresas monitoradas`,trend:"td",trendTxt:"▼ vs 2023",vColor:"var(--red)"},
              {label:"Empresas Críticas",value:criticas,accent:"var(--red)",icon:"🏭",sub:"variação < -40%",trend:"td",trendTxt:`${criticas} urgentes`,vColor:"var(--red)"},
            ].map((k,i)=>(
              <div className="kpi-card" key={i}>
                <div className="card-accent" style={{background:k.accent}}/><div className="card-icon">{k.icon}</div>
                <div className="card-label">{k.label}</div><div className="card-value" style={{color:k.vColor||"var(--navy)"}}>{k.value}</div>
                <div className="card-sub"><span className={`trend ${k.trend}`}>{k.trendTxt}</span>{k.sub}</div>
              </div>
            ))}
          </div>
          <div className="table-card">
            <div className="table-header"><div className="table-title">📉 Empresas em Queda VAF – Todas</div>
              <button className="btn btn-primary btn-sm" onClick={()=>setPage("vaf")}>Ver Módulo Completo →</button>
            </div>
            <div className="tw-wrap"><table>
              <thead><tr><th>#</th><th>Empresa</th><th>VAF Anterior</th><th>VAF Atual</th><th>Variação</th><th>Perda</th><th>Status</th><th>Prazo</th></tr></thead>
              <tbody>{[...empresas].sort((a,b)=>b.perda-a.perda).map((e,i)=>(
                <tr key={e.id}>
                  <td><strong style={{color:i<2?"var(--red)":"var(--gold)"}}>{i+1}º</strong></td>
                  <td><div className="bold">{e.nome}</div><div className="mono" style={{fontSize:10.5,color:"var(--text-muted)"}}>{e.cnpj}</div></td>
                  <td className="mono">{fmt(e.vafAnt)}</td><td className="mono">{fmt(e.vafAt)}</td>
                  <td><span className={`trend ${e.variacao<0?"td":"tu"}`}>{fmtP(e.variacao)}</span></td>
                  <td className="mono bold" style={{color:e.perda>300000?"var(--red)":"var(--gold)"}}>{fmt(e.perda)}</td>
                  <td><Badge s={e.status}/></td>
                  <td style={{fontWeight:e.prazo<45?700:400,color:e.prazo<45?"var(--red)":"var(--text-muted)"}}>{e.prazo}d</td>
                </tr>
              ))}</tbody>
            </table></div>
          </div>
        </div>
      )}

      {tab==="ibs"&&(
        <div>
          <div className="cards-grid" style={{gridTemplateColumns:"repeat(3,1fr)"}}>
            {[
              {label:"IBS Estimado Total",value:fmtK(contratos.reduce((s,c)=>s+(c.ibs||0),0)),accent:"var(--cyan)",icon:"🔮",sub:"base: contratos 2024",trend:"tu",trendTxt:"▲ projeção 2027"},
              {label:"Contratos Ativos",value:contratos.filter(c=>c.status==="ativo").length,accent:"var(--green)",icon:"📋",sub:"com destino IBS definido",trend:"tu",trendTxt:"Ativos"},
              {label:"Sem Destino IBS",value:contratos.filter(c=>c.status==="sem-destino").length,accent:"var(--gold)",icon:"⚠",sub:"valor não vinculado",trend:"tw",trendTxt:"Pendente",vColor:"var(--gold)"},
            ].map((k,i)=>(
              <div className="kpi-card" key={i}>
                <div className="card-accent" style={{background:k.accent}}/><div className="card-icon">{k.icon}</div>
                <div className="card-label">{k.label}</div><div className="card-value" style={{color:k.vColor||"var(--navy)"}}>{k.value}</div>
                <div className="card-sub"><span className={`trend ${k.trend}`}>{k.trendTxt}</span>{k.sub}</div>
              </div>
            ))}
          </div>
          <div className="table-card">
            <div className="table-header"><div className="table-title">🔮 Contratos IBS – Visão Geral</div>
              <button className="btn btn-primary btn-sm" onClick={()=>setPage("ibs")}>Ver Módulo Completo →</button>
            </div>
            <div className="tw-wrap"><table>
              <thead><tr><th>Número</th><th>Fornecedor</th><th>Valor</th><th>Secretaria</th><th>IBS Est.</th><th>Status</th></tr></thead>
              <tbody>{contratos.map(c=>(
                <tr key={c.id}>
                  <td className="mono bold">{c.numero}</td><td>{c.fornecedor}</td>
                  <td className="mono">{fmt(c.valor)}</td><td>{c.secretaria}</td>
                  <td className="mono" style={{color:"var(--blue)",fontWeight:600}}>{fmt(c.ibs)}</td>
                  <td><Badge s={c.status}/></td>
                </tr>
              ))}</tbody>
            </table></div>
          </div>
        </div>
      )}

      {tab==="territorial"&&<Mapa/>}
    </div>
  );
}

// ── VAF ──────────────────────────────────────────────────────────────────────
function Vaf({empresas,setEmpresas,showToast}){
  const [sub,setSub]=useState("ranking");
  const [showForm,setShowForm]=useState(false);
  const [f,setF]=useState({cnpj:"",nome:"",vafAnt:"",vafAt:"",prazo:""});

  const salvar=()=>{
    if(!f.cnpj||!f.nome||!f.vafAt){showToast("Preencha CNPJ, Nome e VAF Atual","error");return;}
    const a=parseFloat(f.vafAnt)||0, b=parseFloat(f.vafAt)||0;
    const variacao=a?((b-a)/a*100):0;
    const status=variacao<-40?"critico":variacao<-25?"atencao":"monitorar";
    const nova={id:Date.now(),...f,vafAnt:a,vafAt:b,variacao:parseFloat(variacao.toFixed(1)),perda:Math.max(0,a-b),status,prazo:parseInt(f.prazo)||90};
    addEmpresa(nova).then(docRef=>{ setEmpresas(p=>[{id:docRef.id,...nova},...p]); }).catch(()=>{ setEmpresas(p=>[nova,...p]); });
    setF({cnpj:"",nome:"",vafAnt:"",vafAt:"",prazo:""});
    setShowForm(false);
    showToast("Empresa cadastrada com sucesso!","success");
  };

  return(
    <div>
      <div className="page-header">
        <div><div className="page-title">VAF – Valor Adicionado Fiscal</div><div className="page-sub">Monitoramento ICMS e proteção de receita</div></div>
        <div className="page-actions">
          <button className="btn btn-outline" onClick={()=>setSub("importacao")}>📥 Importar SEF</button>
          <button className="btn btn-primary" onClick={()=>setShowForm(v=>!v)}>+ Nova Empresa</button>
        </div>
      </div>

      <div className="tab-nav">
        {[["ranking","🏆 Ranking"],["contestacao","⚡ Contestação"],["importacao","📥 Importação"]].map(([k,l])=>(
          <button key={k} className={`tab-btn ${sub===k?"active":""}`} onClick={()=>setSub(k)}>{l}</button>
        ))}
      </div>

      {showForm&&(
        <div className="form-card">
          <div className="form-title">🏭 Cadastrar Empresa VAF</div>
          <div className="fg">
            <div className="fgi"><label className="req">CNPJ</label><input className="mono" placeholder="00.000.000/0001-00" value={f.cnpj} onChange={e=>setF({...f,cnpj:e.target.value})}/></div>
            <div className="fgi"><label className="req">Razão Social</label><input placeholder="Nome da empresa" value={f.nome} onChange={e=>setF({...f,nome:e.target.value})}/></div>
            <div className="fgi"><label>VAF Ano Anterior (R$)</label><div className="igroup"><span className="iprefix">R$</span><input type="number" placeholder="0" value={f.vafAnt} onChange={e=>setF({...f,vafAnt:e.target.value})}/></div></div>
            <div className="fgi"><label className="req">VAF Ano Atual (R$)</label><div className="igroup"><span className="iprefix">R$</span><input type="number" placeholder="0" value={f.vafAt} onChange={e=>setF({...f,vafAt:e.target.value})}/></div></div>
            <div className="fgi"><label>Prazo Contestação (dias)</label><input type="number" placeholder="90" value={f.prazo} onChange={e=>setF({...f,prazo:e.target.value})}/></div>
          </div>
          {f.vafAnt&&f.vafAt&&(
            <div style={{marginTop:12,padding:"9px 12px",background:"rgba(26,111,196,0.06)",borderRadius:8,fontSize:12.5}}>
              Variação calculada: <strong style={{color:((parseFloat(f.vafAt)-parseFloat(f.vafAnt))/parseFloat(f.vafAnt)*100)<0?"var(--red)":"var(--green)"}}>{fmtP((parseFloat(f.vafAt)-parseFloat(f.vafAnt))/(parseFloat(f.vafAnt)||1)*100)}</strong>
              &nbsp;·&nbsp;Perda: <strong>{fmt(Math.max(0,parseFloat(f.vafAnt)-parseFloat(f.vafAt)))}</strong>
            </div>
          )}
          <div className="form-actions">
            <button className="btn btn-ghost" onClick={()=>setShowForm(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={salvar}>✓ Salvar Empresa</button>
          </div>
        </div>
      )}

      {sub==="ranking"&&(
        <div className="table-card">
          <div className="table-header">
            <div className="table-title">🏆 Ranking de Perdas VAF</div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn btn-outline btn-sm" onClick={()=>exportRankingVAF(empresas)}>📊 Exportar Excel</button>
              <button className="btn btn-primary btn-sm" onClick={()=>setSub("contestacao")}>⚡ Iniciar Contestação</button>
            </div>
          </div>
          <div className="tw-wrap">
            <table>
              <thead><tr><th>#</th><th>Empresa / CNPJ</th><th>VAF Anterior</th><th>VAF Atual</th><th>Variação</th><th>Perda Potencial</th><th>Status</th><th>Prazo</th></tr></thead>
              <tbody>
                {[...empresas].sort((a,b)=>b.perda-a.perda).map((e,i)=>(
                  <tr key={e.id}>
                    <td><strong style={{color:i<2?"var(--red)":"var(--gold)"}}>{i+1}º</strong></td>
                    <td><div className="bold">{e.nome}</div><div className="mono" style={{fontSize:10.5,color:"var(--text-muted)"}}>{e.cnpj}</div></td>
                    <td className="mono">{fmt(e.vafAnt)}</td>
                    <td className="mono">{fmt(e.vafAt)}</td>
                    <td><span className={`trend ${e.variacao<0?"td":"tu"}`}>{fmtP(e.variacao)}</span></td>
                    <td className="mono bold" style={{color:e.perda>300000?"var(--red)":"var(--gold)"}}>{fmt(e.perda)}</td>
                    <td><Badge s={e.status}/></td>
                    <td style={{fontWeight:e.prazo<45?700:400,color:e.prazo<45?"var(--red)":"var(--text-muted)"}}>{e.prazo} dias</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sub==="contestacao"&&<Contestacao empresas={empresas.filter(e=>e.status!=="monitorar")} showToast={showToast}/>}
      {sub==="importacao"&&<ImportacaoSEF showToast={showToast}/>}
    </div>
  );
}

function Contestacao({empresas,showToast}){
  const [sel,setSel]=useState([]);
  const [f,setF]=useState({justificativa:"",responsavel:"",dataEnvio:""});
  const toggle=id=>setSel(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const enviar=()=>{
    if(!sel.length){showToast("Selecione ao menos uma empresa","error");return;}
    if(!f.justificativa){showToast("Informe a justificativa","error");return;}
    // Firebase: addDoc(collection(db,"vaf_alertas"),{empresas:sel,...f})
    showToast(`Contestação registrada para ${sel.length} empresa(s)!`,"success");
    setSel([]);
  };
  return(
    <div className="form-card">
      <div className="form-title">⚡ Proposta de Contestação VAF</div>
      <div style={{marginBottom:14,fontSize:12.5,color:"var(--text-muted)"}}>Selecione as empresas para contestação:</div>
      <div style={{display:"grid",gap:8,marginBottom:18}}>
        {empresas.map(e=>(
          <div key={e.id} onClick={()=>toggle(e.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:9,border:`1.5px solid ${sel.includes(e.id)?"var(--blue)":"var(--border)"}`,background:sel.includes(e.id)?"rgba(26,111,196,0.05)":"var(--surface)",cursor:"pointer",transition:"all 0.15s"}}>
            <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${sel.includes(e.id)?"var(--blue)":"#ccc"}`,background:sel.includes(e.id)?"var(--blue)":"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {sel.includes(e.id)&&<span style={{color:"white",fontSize:10,lineHeight:1}}>✓</span>}
            </div>
            <div style={{flex:1}}><div className="bold" style={{fontSize:13}}>{e.nome}</div><div style={{fontSize:11,color:"var(--text-muted)"}}>{e.cnpj} · Perda: {fmt(e.perda)} · Prazo: {e.prazo} dias</div></div>
            <Badge s={e.status}/>
          </div>
        ))}
      </div>
      <div className="fg">
        <div className="fgi fg-full"><label className="req">Justificativa Técnica</label><textarea placeholder="Descreva os fundamentos da contestação, variações identificadas e impacto no repasse ICMS..." value={f.justificativa} onChange={e=>setF({...f,justificativa:e.target.value})}/></div>
        <div className="fgi"><label>Responsável</label><input placeholder="Nome do servidor" value={f.responsavel} onChange={e=>setF({...f,responsavel:e.target.value})}/></div>
        <div className="fgi"><label>Data Prevista</label><input type="date" value={f.dataEnvio} onChange={e=>setF({...f,dataEnvio:e.target.value})}/></div>
      </div>
      <div className="form-actions">
        <div style={{fontSize:12,color:"var(--text-muted)",marginRight:"auto"}}>{sel.length} empresa(s) selecionada(s)</div>
        <button className="btn btn-primary" onClick={enviar}>📤 Registrar Contestação</button>
      </div>
    </div>
  );
}

function ImportacaoSEF({showToast}){
  const [file,setFile]=useState(null);
  const [preview,setPreview]=useState(null);
  const mock=[
    {cnpj:"00.000.001/0001-00",empresa:"Metalúrgica ABC Ltda",vafAnt:1240000,vafAt:612000},
    {cnpj:"00.000.007/0001-00",empresa:"Nova Empresa Ltda",vafAnt:450000,vafAt:320000},
  ];
  const handleFile=f=>{setFile(f);importSEF(f).then(rows=>{setPreview(rows);showToast(`"${f.name}" carregado! ${rows.length} registros`,"success");}).catch(()=>{setPreview(mock);showToast(`"${f.name}" carregado (modo demo)`,"success");});};
  return(
    <div>
      <div className="form-card">
        <div className="form-title">📥 Importação de Dados SEF</div>
        <div className="drop-zone" onClick={()=>document.getElementById("fup").click()}>
          <input id="fup" type="file" accept=".xlsx,.csv" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
          <div style={{fontSize:32,marginBottom:10}}>{file?"✅":"📂"}</div>
          <div style={{fontWeight:700,fontSize:14,marginBottom:5}}>{file?file.name:"Arraste o arquivo ou clique para selecionar"}</div>
          <div style={{fontSize:12,color:"var(--text-muted)"}}>Formatos aceitos: .xlsx, .csv</div>
        </div>
        <div style={{marginTop:16,padding:"12px 14px",background:"var(--surface)",borderRadius:9,fontSize:12}}>
          <strong style={{display:"block",marginBottom:8}}>📋 Estrutura esperada do arquivo VAF:</strong>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}}>
            {["CNPJ","Empresa / Razão Social","VAF Ano Anterior","VAF Ano Atual"].map((c,i)=>(
              <div key={i} style={{background:"white",padding:"5px 9px",borderRadius:6,border:"1px solid var(--border)",fontFamily:"IBM Plex Mono,monospace",fontSize:10.5}}>{c}</div>
            ))}
          </div>
        </div>
      </div>
      {preview&&(
        <div className="table-card">
          <div className="table-header">
            <div className="table-title">📊 Prévia dos dados importados</div>
            <button className="btn btn-primary btn-sm" onClick={()=>showToast("Dados importados!","success")}>✓ Confirmar Importação</button>
          </div>
          <div className="tw-wrap">
            <table>
              <thead><tr><th>CNPJ</th><th>Empresa</th><th>VAF Anterior</th><th>VAF Atual</th><th>Variação</th></tr></thead>
              <tbody>
                {preview.map((r,i)=>{const v=r.vafAnt?((r.vafAt-r.vafAnt)/r.vafAnt*100):0;return(
                  <tr key={i}>
                    <td className="mono">{r.cnpj}</td><td className="bold">{r.empresa}</td>
                    <td className="mono">{fmt(r.vafAnt)}</td><td className="mono">{fmt(r.vafAt)}</td>
                    <td><span className={`trend ${v<0?"td":"tu"}`}>{fmtP(v)}</span></td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── IBS ──────────────────────────────────────────────────────────────────────
function Ibs({contratos,setContratos,showToast}){
  const [sub,setSub]=useState("contratos");
  const [showForm,setShowForm]=useState(false);
  const [f,setF]=useState({numero:"",fornecedor:"",valor:"",secretaria:"",municipio:"",status:"ativo"});

  const salvar=()=>{
    if(!f.numero||!f.fornecedor||!f.valor){showToast("Preencha os campos obrigatórios","error");return;}
    const valor=parseFloat(f.valor)||0;
    const novo={id:Date.now(),...f,valor,ibs:valor*0.084};
    addContrato(novo).then(docRef=>{ setContratos(p=>[{id:docRef.id,...novo},...p]); }).catch(()=>{ setContratos(p=>[novo,...p]); });
    setF({numero:"",fornecedor:"",valor:"",secretaria:"",municipio:"",status:"ativo"});
    setShowForm(false);
    showToast("Contrato cadastrado!","success");
  };

  const totalValor=contratos.reduce((s,c)=>s+c.valor,0);
  const totalIbs=contratos.reduce((s,c)=>s+(c.ibs||0),0);
  const porSec=contratos.reduce((acc,c)=>{acc[c.secretaria]=(acc[c.secretaria]||0)+c.valor;return acc;},{});

  return(
    <div>
      <div className="page-header">
        <div><div className="page-title">IBS – Imposto sobre Bens e Serviços</div><div className="page-sub">Reforma Tributária · Preparação e Simulação 2027</div></div>
        <div className="page-actions">
          <button className="btn btn-outline" onClick={()=>setSub("simulacao")}>📊 Simulação</button>
          <button className="btn btn-primary" onClick={()=>setShowForm(v=>!v)}>+ Novo Contrato</button>
        </div>
      </div>

      <div className="cards-grid" style={{gridTemplateColumns:"repeat(3,1fr)",marginBottom:18}}>
        {[{label:"Volume Contratado",value:fmtK(totalValor),icon:"💼",accent:"var(--blue)"},
          {label:"IBS Estimado Total",value:fmtK(totalIbs),icon:"🔮",accent:"var(--cyan)",vColor:"var(--blue)"},
          {label:"Contratos Cadastrados",value:contratos.length,icon:"📋",accent:"var(--green)"}].map((k,i)=>(
          <div className="kpi-card" key={i}>
            <div className="card-accent" style={{background:k.accent}}/><div className="card-icon">{k.icon}</div>
            <div className="card-label">{k.label}</div><div className="card-value" style={{color:k.vColor||"var(--navy)"}}>{k.value}</div>
          </div>
        ))}
      </div>

      <div className="tab-nav">
        {[["contratos","📋 Contratos"],["pagamentos","💳 Pagamentos"],["simulacao","📊 Simulação"]].map(([k,l])=>(
          <button key={k} className={`tab-btn ${sub===k?"active":""}`} onClick={()=>setSub(k)}>{l}</button>
        ))}
      </div>

      {showForm&&(
        <div className="form-card">
          <div className="form-title">📋 Cadastrar Contrato IBS</div>
          <div className="fg">
            <div className="fgi"><label className="req">Número do Contrato</label><input className="mono" placeholder="CT-2024-000" value={f.numero} onChange={e=>setF({...f,numero:e.target.value})}/></div>
            <div className="fgi"><label className="req">Fornecedor</label><input placeholder="Razão Social ou CNPJ" value={f.fornecedor} onChange={e=>setF({...f,fornecedor:e.target.value})}/></div>
            <div className="fgi"><label className="req">Valor (R$)</label><div className="igroup"><span className="iprefix">R$</span><input type="number" placeholder="0" value={f.valor} onChange={e=>setF({...f,valor:e.target.value})}/></div></div>
            <div className="fgi"><label>Secretaria</label>
              <select value={f.secretaria} onChange={e=>setF({...f,secretaria:e.target.value})}>
                <option value="">Selecione...</option>
                {["Saúde","Educação","Obras","Meio Ambiente","Administração","Fazenda","Assistência Social"].map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="fgi"><label>Município de Consumo</label><input placeholder="Município-UF" value={f.municipio} onChange={e=>setF({...f,municipio:e.target.value})}/></div>
            <div className="fgi"><label>Status</label>
              <select value={f.status} onChange={e=>setF({...f,status:e.target.value})}>
                <option value="ativo">Ativo</option><option value="pendente">Pendente</option><option value="sem-destino">Sem Destino IBS</option>
              </select>
            </div>
          </div>
          {f.valor&&<div style={{marginTop:10,padding:"8px 12px",background:"rgba(26,111,196,0.06)",borderRadius:8,fontSize:12.5}}>IBS estimado (8,4%): <strong className="mono">{fmt(parseFloat(f.valor)*0.084)}</strong></div>}
          <div className="form-actions">
            <button className="btn btn-ghost" onClick={()=>setShowForm(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={salvar}>✓ Salvar Contrato</button>
          </div>
        </div>
      )}

      {sub==="contratos"&&(
        <div className="table-card">
          <div className="table-header"><div className="table-title">📋 Contratos IBS</div><button className="btn btn-outline btn-sm" onClick={()=>exportContratos(contratos)}>📊 Exportar</button></div>
          <div className="tw-wrap">
            <table>
              <thead><tr><th>Número</th><th>Fornecedor</th><th>Valor</th><th>Secretaria</th><th>Município</th><th>IBS Estimado</th><th>Status</th></tr></thead>
              <tbody>
                {contratos.map(c=>(
                  <tr key={c.id}>
                    <td className="mono bold">{c.numero}</td><td>{c.fornecedor}</td>
                    <td className="mono">{fmt(c.valor)}</td><td>{c.secretaria||"—"}</td><td>{c.municipio||"—"}</td>
                    <td className="mono bold" style={{color:"var(--blue)"}}>{c.ibs?fmt(c.ibs):"—"}</td>
                    <td><Badge s={c.status}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sub==="pagamentos"&&<PagIbs contratos={contratos} showToast={showToast}/>}

      {sub==="simulacao"&&(
        <div className="form-card">
          <div className="form-title">📊 Simulação IBS por Secretaria</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:14,marginBottom:18}}>
            {Object.entries(porSec).map(([sec,val])=>(
              <div key={sec} style={{padding:"12px 14px",background:"var(--surface)",borderRadius:9,border:"1px solid var(--border)"}}>
                <div style={{fontSize:11.5,color:"var(--text-muted)",marginBottom:5}}>{sec}</div>
                <div className="mono" style={{fontSize:17,fontWeight:700,color:"var(--navy)"}}>{fmtK(val)}</div>
                <div style={{fontSize:11.5,color:"var(--blue)",marginTop:3}}>IBS: {fmtK(val*0.084)}</div>
                <div className="pb" style={{marginTop:7}}><div className="pf" style={{width:`${(val/totalValor*100).toFixed(0)}%`}}/></div>
              </div>
            ))}
          </div>
          <div style={{padding:"14px 16px",background:"rgba(26,111,196,0.05)",borderRadius:9,display:"flex",gap:24,flexWrap:"wrap"}}>
            <div><div style={{fontSize:10.5,color:"var(--text-muted)"}}>TOTAL CONTRATOS</div><div className="mono" style={{fontSize:20,fontWeight:700}}>{fmtK(totalValor)}</div></div>
            <div><div style={{fontSize:10.5,color:"var(--text-muted)"}}>IBS ESTIMADO (8,4%)</div><div className="mono" style={{fontSize:20,fontWeight:700,color:"var(--blue)"}}>{fmtK(totalIbs)}</div></div>
            <div><div style={{fontSize:10.5,color:"var(--text-muted)"}}>ALÍQUOTA</div><div className="mono" style={{fontSize:20,fontWeight:700,color:"var(--cyan)"}}>8,4%</div></div>
          </div>
        </div>
      )}
    </div>
  );
}

function PagIbs({contratos,showToast}){
  const [pags,setPags]=useState([{id:1,contrato:"CT-2024-001",valor:95000,data:"2024-03-15",desc:"1ª Parcela"},{id:2,contrato:"CT-2024-002",valor:230000,data:"2024-04-01",desc:"Medição Mensal"}]);
  const [f,setF]=useState({contratoId:"",valor:"",data:"",desc:""});
  const salvar=()=>{
    if(!f.contratoId||!f.valor){showToast("Preencha contrato e valor","error");return;}
    const c=contratos.find(x=>x.id===parseInt(f.contratoId));
    const novo={id:Date.now(),contrato:c?.numero||"—",valor:parseFloat(f.valor),data:f.data,desc:f.desc};
    // Firebase: addDoc(collection(db,"ibs_pagamentos"),novo)
    setPags(p=>[novo,...p]);setF({contratoId:"",valor:"",data:"",desc:""});showToast("Pagamento registrado!","success");
  };
  return(
    <div>
      <div className="form-card">
        <div className="form-title">💳 Lançar Pagamento IBS</div>
        <div className="fg">
          <div className="fgi"><label className="req">Contrato</label>
            <select value={f.contratoId} onChange={e=>setF({...f,contratoId:e.target.value})}>
              <option value="">Selecione...</option>{contratos.map(c=><option key={c.id} value={c.id}>{c.numero} – {c.fornecedor}</option>)}
            </select>
          </div>
          <div className="fgi"><label className="req">Valor (R$)</label><div className="igroup"><span className="iprefix">R$</span><input type="number" placeholder="0" value={f.valor} onChange={e=>setF({...f,valor:e.target.value})}/></div></div>
          <div className="fgi"><label>Data</label><input type="date" value={f.data} onChange={e=>setF({...f,data:e.target.value})}/></div>
          <div className="fgi"><label>Descrição</label><input placeholder="Ex: 1ª parcela..." value={f.desc} onChange={e=>setF({...f,desc:e.target.value})}/></div>
        </div>
        <div className="form-actions"><button className="btn btn-primary" onClick={salvar}>✓ Registrar Pagamento</button></div>
      </div>
      <div className="table-card">
        <div className="table-header"><div className="table-title">💳 Histórico de Pagamentos</div></div>
        <div className="tw-wrap"><table><thead><tr><th>Contrato</th><th>Valor</th><th>Data</th><th>Descrição</th></tr></thead>
          <tbody>{pags.map(p=><tr key={p.id}><td className="mono bold">{p.contrato}</td><td className="mono" style={{color:"var(--blue)",fontWeight:600}}>{fmt(p.valor)}</td><td>{p.data||"—"}</td><td>{p.desc||"—"}</td></tr>)}</tbody>
        </table></div>
      </div>
    </div>
  );
}

// ── MAPA ─────────────────────────────────────────────────────────────────────
function Mapa(){
  const [layer,setLayer]=useState("empresas");
  const [tooltip,setTooltip]=useState(null);
  const allPins=[
    {x:"35%",y:"42%",c:"var(--red)",l:"Metalúrgica ABC",types:["empresas","alertas"],size:14},
    {x:"57%",y:"55%",c:"var(--red)",l:"Distribuidora XYZ",types:["empresas","alertas"],size:14},
    {x:"46%",y:"32%",c:"var(--gold)",l:"Comércio Regional",types:["empresas","volume"],size:12},
    {x:"66%",y:"46%",c:"var(--green)",l:"Indústria Nova",types:["empresas"],size:10},
    {x:"30%",y:"62%",c:"var(--gold)",l:"Transportes Delta",types:["empresas","volume"],size:11},
    {x:"51%",y:"70%",c:"var(--green)",l:"Agropecuária Sul",types:["empresas"],size:10},
    {x:"44%",y:"52%",c:"#1A6FC4",l:"CT-2024-001 · TechService R$380K",types:["contratos","volume"],size:11},
    {x:"61%",y:"38%",c:"#2484E4",l:"CT-2024-002 · Construções Beta R$920K",types:["contratos","volume"],size:15},
    {x:"40%",y:"66%",c:"#00C2D4",l:"CT-2024-004 · Saúde Integral R$680K",types:["contratos"],size:13},
    {x:"53%",y:"48%",c:"#00C2D4",l:"CT-2024-003 · EduTech R$240K",types:["contratos"],size:10},
  ];
  const pins=allPins.filter(p=>p.types.includes(layer));
  const legendByLayer={
    empresas:[["var(--red)","Crítico (queda >40%)"],["var(--gold)","Atenção (queda 25-40%)"],["var(--green)","Monitorar (<25%)"]],
    contratos:[["#2484E4","Alto Volume (>R$500K)"],["#1A6FC4","Médio Volume"],["#00C2D4","Baixo Volume"]],
    volume:[["var(--red)","Perda VAF alta"],["#2484E4","Contrato alto valor"],["var(--gold)","Volume médio"]],
    alertas:[["var(--red)","Alerta Crítico VAF"],["var(--gold)","Alerta de Atenção"],["var(--green)","Sem alerta"]],
  };
  return(
    <div>
      <div className="page-header">
        <div><div className="page-title">Mapa Territorial</div><div className="page-sub">Visualização espacial de empresas, contratos e alertas por região</div></div>
        <div className="page-actions">
          <div style={{fontSize:12,color:"var(--text-muted)",display:"flex",alignItems:"center",gap:6}}>
            <span>📍 {pins.length} pontos visíveis</span>
          </div>
        </div>
      </div>
      <div className="map-container" style={{minHeight:420}}>
        <svg width="100%" height="420" viewBox="0 0 800 420" style={{position:"absolute",inset:0}}>
          <defs><linearGradient id="mg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#0d2240"/><stop offset="100%" stopColor="#0a1a2f"/></linearGradient></defs>
          <rect width="800" height="420" fill="url(#mg)"/>
          {[0,1,2,3,4,5,6,7].map(i=><line key={`v${i}`} x1={i*115} y1="0" x2={i*115} y2="420" stroke="rgba(255,255,255,0.025)" strokeWidth="1"/>)}
          {[0,1,2,3,4,5].map(i=><line key={`h${i}`} x1="0" y1={i*84} x2="800" y2={i*84} stroke="rgba(255,255,255,0.025)" strokeWidth="1"/>)}
          <polygon points="200,90 570,70 630,190 600,360 370,400 160,340 140,190" fill="rgba(26,111,196,0.07)" stroke="rgba(36,132,228,0.25)" strokeWidth="1.5"/>
          <polygon points="200,90 350,80 330,210 175,240" fill="rgba(0,194,212,0.05)" stroke="rgba(0,194,212,0.18)" strokeWidth="1"/>
          <polygon points="350,80 570,70 550,210 330,210" fill="rgba(245,166,35,0.04)" stroke="rgba(245,166,35,0.15)" strokeWidth="1"/>
          <polygon points="175,240 330,210 370,400 160,340" fill="rgba(45,198,83,0.04)" stroke="rgba(45,198,83,0.15)" strokeWidth="1"/>
          <text x="248" y="170" textAnchor="middle" fontSize="10" fill="rgba(0,194,212,0.4)" fontFamily="Sora,sans-serif">Região Norte</text>
          <text x="450" y="145" textAnchor="middle" fontSize="10" fill="rgba(245,166,35,0.4)" fontFamily="Sora,sans-serif">Região Leste</text>
          <text x="265" y="320" textAnchor="middle" fontSize="10" fill="rgba(45,198,83,0.4)" fontFamily="Sora,sans-serif">Região Sul</text>
        </svg>
        <div style={{position:"absolute",top:14,left:14,display:"flex",gap:7,flexWrap:"wrap",zIndex:5}}>
          {[["empresas","🏭 Empresas VAF"],["contratos","📋 Contratos IBS"],["volume","📊 Volume"],["alertas","🔴 Alertas"]].map(([k,l])=>(
            <button key={k} className={`map-filter-btn ${layer===k?"active":""}`} onClick={()=>setLayer(k)}>{l}</button>
          ))}
        </div>
        {pins.map((p,i)=>(
          <div key={i} className="map-pin" style={{left:p.x,top:p.y,background:p.c,zIndex:10,width:p.size,height:p.size}}
            title={p.l}
            onMouseEnter={()=>setTooltip({l:p.l,x:p.x,y:p.y})}
            onMouseLeave={()=>setTooltip(null)}
          />
        ))}
        {tooltip&&(
          <div style={{position:"absolute",left:tooltip.x,top:`calc(${tooltip.y} - 36px)`,background:"rgba(11,31,58,0.95)",color:"white",fontSize:11,padding:"5px 10px",borderRadius:6,zIndex:20,whiteSpace:"nowrap",pointerEvents:"none",border:"1px solid rgba(255,255,255,0.1)"}}>
            {tooltip.l}
          </div>
        )}
        <div style={{position:"absolute",top:14,right:14,background:"rgba(11,31,58,0.88)",borderRadius:9,padding:"12px 14px",zIndex:5}}>
          <div style={{fontSize:9,fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:9}}>Legenda</div>
          {(legendByLayer[layer]||[]).map(([c,l])=>(
            <div key={l} style={{display:"flex",alignItems:"center",gap:7,fontSize:11.5,color:"rgba(255,255,255,0.7)",marginBottom:6}}><div style={{width:9,height:9,borderRadius:"50%",background:c}}/>{l}</div>
          ))}
        </div>
        {pins.length===0&&(
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:"rgba(255,255,255,0.4)",zIndex:5}}>
            Nenhum ponto para esta camada
          </div>
        )}
      </div>
    </div>
  );
}

// ── RELATÓRIOS ────────────────────────────────────────────────────────────────
function Relatorios({empresas,contratos,showToast}){
  const [tipo,setTipo]=useState("vaf");
  const [f,setF]=useState({periodo:"2024",municipio:"Exemplo-MG",responsavel:""});
  return(
    <div>
      <div className="page-header"><div><div className="page-title">Relatórios Oficiais</div><div className="page-sub">Geração de relatórios PDF com validação institucional</div></div></div>
      <div style={{display:"grid",gridTemplateColumns:"260px 1fr",gap:18}}>
        <div>
          <div className="table-card" style={{overflow:"hidden"}}>
            <div style={{padding:"12px 14px",borderBottom:"1px solid var(--border)",fontWeight:700,fontSize:13,color:"var(--navy)"}}>Tipo de Relatório</div>
            {[["vaf","📉","VAF – ICMS","Ranking, perdas, contestação"],["ibs","🔮","IBS","Volume, estimativa, contratos"],["territorial","🗺","Territorial","Indicadores regionais"]].map(([k,ic,t,d])=>(
              <div key={k} onClick={()=>setTipo(k)} style={{padding:"11px 14px",cursor:"pointer",borderLeft:`3px solid ${tipo===k?"var(--blue)":"transparent"}`,background:tipo===k?"rgba(26,111,196,0.04)":"transparent",borderBottom:"1px solid var(--border)",transition:"all 0.15s"}}>
                <div style={{fontSize:13,marginBottom:2}}>{ic} <strong>{t}</strong></div>
                <div style={{fontSize:11,color:"var(--text-muted)"}}>{d}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="form-card">
            <div className="form-title">⚙️ Configurações</div>
            <div className="fg">
              <div className="fgi"><label>Período</label><select value={f.periodo} onChange={e=>setF({...f,periodo:e.target.value})}>{["2024","2023","2022"].map(y=><option key={y}>{y}</option>)}</select></div>
              <div className="fgi"><label>Município</label><input value={f.municipio} onChange={e=>setF({...f,municipio:e.target.value})}/></div>
              <div className="fgi fg-full"><label>Responsável</label><input placeholder="Nome / cargo" value={f.responsavel} onChange={e=>setF({...f,responsavel:e.target.value})}/></div>
            </div>
            <div style={{margin:"18px 0",padding:16,background:"var(--surface)",borderRadius:9,border:"1px solid var(--border)"}}>
              <div style={{fontSize:10.5,fontWeight:700,color:"var(--text-muted)",marginBottom:10,letterSpacing:1,textTransform:"uppercase"}}>Preview PDF (A4)</div>
              <div style={{background:"white",borderRadius:8,padding:"14px 18px",border:"1px solid var(--border)",fontFamily:"serif"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",borderBottom:"2px solid var(--navy)",paddingBottom:10,marginBottom:10}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}><div style={{width:36,height:36,background:"var(--navy)",borderRadius:7,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:800,fontSize:12}}>GV</div><div><div style={{fontSize:13,fontWeight:700,color:"var(--navy)"}}>GERENCIADOR VAF IBS</div><div style={{fontSize:9.5,color:"var(--text-muted)"}}>Gestão VAF · IBS · ICMS</div></div></div>
                  <div style={{textAlign:"right",fontSize:11,color:"var(--text-muted)"}}><div style={{fontWeight:700,color:"var(--navy)"}}>{f.municipio}</div><div>Período: {f.periodo}</div><div>{new Date().toLocaleDateString("pt-BR")}</div></div>
                </div>
                <div style={{fontSize:12.5,fontWeight:700,textAlign:"center",color:"var(--navy)",marginBottom:6}}>
                  {{vaf:"RELATÓRIO VAF / ICMS",ibs:"RELATÓRIO IBS – REFORMA TRIBUTÁRIA",territorial:"RELATÓRIO TERRITORIAL"}[tipo]}
                </div>
                <div style={{fontSize:10,color:"var(--text-muted)",display:"flex",gap:14,flexWrap:"wrap"}}>
                  <span>Empresas: {empresas.length}</span><span>Contratos: {contratos.length}</span><span>Perda: {fmtK(empresas.reduce((s,e)=>s+e.perda,0))}</span>
                </div>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-outline" onClick={()=>{tipo==="vaf"?exportRankingVAF(empresas):exportContratos(contratos);showToast("Excel exportado!","success");}}>📊 Exportar Excel</button>
              <button className="btn btn-primary" onClick={()=>{tipo==="vaf"?gerarRelatorioVAF(empresas,{municipio:f.municipio,periodo:f.periodo}):gerarRelatorioIBS(contratos,{municipio:f.municipio,periodo:f.periodo});showToast("PDF gerado!","success");}}>📄 Gerar PDF Oficial</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ADMINISTRAÇÃO ─────────────────────────────────────────────────────────────
function Admin({showToast}){
  const [sub,setSub]=useState("municipio");
  const [muni,setMuni]=useState({nome:"Exemplo",uf:"MG",anoBase:"2024",repasseIcms:"8420000",responsavel:"João Silva",email:"fazenda@exemplo.mg.gov.br"});
  const [userList,setUserList]=useState([{id:1,nome:"João Silva",email:"joao@exemplo.mg.gov.br",perfil:"admin",cargo:"Secretário de Fazenda"},{id:2,nome:"Maria Santos",email:"maria@exemplo.mg.gov.br",perfil:"operacional",cargo:"Analista Tributário"},{id:3,nome:"Pedro Costa",email:"pedro@exemplo.mg.gov.br",perfil:"operacional",cargo:"Servidor Fazendário"}]);
  const [showAddUser,setShowAddUser]=useState(false);
  const [newUser,setNewUser]=useState({nome:"",email:"",cargo:"",perfil:"operacional"});
  const salvarUsuario=()=>{
    if(!newUser.nome||!newUser.email){showToast("Preencha nome e e-mail","error");return;}
    const u={id:Date.now(),...newUser};
    setUserList(p=>[...p,u]);
    setNewUser({nome:"",email:"",cargo:"",perfil:"operacional"});
    setShowAddUser(false);
    showToast("Usuário adicionado com sucesso!","success");
  };
  return(
    <div>
      <div className="page-header"><div><div className="page-title">Administração do Sistema</div><div className="page-sub">Configurações do município, usuários e permissões</div></div></div>
      <div className="tab-nav">
        {[["municipio","🏛 Município"],["usuarios","👥 Usuários"],["permissoes","🔐 Permissões"],["pwa","📱 PWA"]].map(([k,l])=>(
          <button key={k} className={`tab-btn ${sub===k?"active":""}`} onClick={()=>setSub(k)}>{l}</button>
        ))}
      </div>

      {sub==="municipio"&&(
        <div className="form-card">
          <div className="form-title">🏛 Dados do Município</div>
          <div className="fg">
            <div className="fgi"><label className="req">Nome do Município</label><input value={muni.nome} onChange={e=>setMuni({...muni,nome:e.target.value})}/></div>
            <div className="fgi"><label>UF</label><select value={muni.uf} onChange={e=>setMuni({...muni,uf:e.target.value})}>{["MG","SP","RJ","ES","GO","MT","MS","BA","PR","SC","RS","CE","PE","PB","RN","AL","SE","PI","MA","PA","AM","AC","RO","RR","AP","TO","DF"].map(u=><option key={u}>{u}</option>)}</select></div>
            <div className="fgi"><label>Ano Base</label><select value={muni.anoBase} onChange={e=>setMuni({...muni,anoBase:e.target.value})}>{["2024","2023","2022"].map(y=><option key={y}>{y}</option>)}</select></div>
            <div className="fgi"><label>Repasse ICMS Anual (R$)</label><div className="igroup"><span className="iprefix">R$</span><input type="number" value={muni.repasseIcms} onChange={e=>setMuni({...muni,repasseIcms:e.target.value})}/></div></div>
            <div className="fgi"><label>Responsável Técnico</label><input value={muni.responsavel} onChange={e=>setMuni({...muni,responsavel:e.target.value})}/></div>
            <div className="fgi"><label>E-mail Fazenda</label><input type="email" value={muni.email} onChange={e=>setMuni({...muni,email:e.target.value})}/></div>
            <div className="fgi"><label>Logo da Prefeitura</label>
              <div className="drop-zone" style={{padding:"14px",textAlign:"center",cursor:"pointer"}} onClick={()=>showToast("Upload de logo: integre Firebase Storage","success")}>
                <div style={{fontSize:24,marginBottom:4}}>🖼️</div><div style={{fontSize:11.5,color:"var(--text-muted)"}}>Clique para fazer upload (.png, .svg)</div>
              </div>
            </div>
          </div>
          <div className="form-actions"><button className="btn btn-primary" onClick={()=>showToast("Configurações salvas!","success")}>✓ Salvar Configurações</button></div>
        </div>
      )}

      {sub==="usuarios"&&(
        <div>
          <div className="table-card">
            <div className="table-header"><div className="table-title">👥 Usuários do Sistema</div><button className="btn btn-primary btn-sm" onClick={()=>setShowAddUser(v=>!v)}>+ Adicionar Usuário</button></div>
            {userList.map(u=>(
              <div className="user-row" key={u.id}>
                <div className="ura">{u.nome.split(" ").map(n=>n[0]).join("").slice(0,2)}</div>
                <div style={{flex:1}}><div className="bold" style={{fontSize:13}}>{u.nome}</div><div style={{fontSize:11.5,color:"var(--text-muted)"}}>{u.email} · {u.cargo}</div></div>
                <Badge s={u.perfil==="admin"?"ativo":"monitorar"}/>
                <span className={`badge ${u.perfil==="admin"?"badge-blue":"badge-gray"}`}>{u.perfil==="admin"?"🔑 Admin":"👤 Operacional"}</span>
                <button className="btn btn-ghost btn-sm" onClick={()=>showToast("Edição de usuário disponível em breve","success")}>Editar</button>
              </div>
            ))}
          </div>
          {showAddUser&&(
            <div className="form-card">
              <div className="form-title">➕ Adicionar Novo Usuário</div>
              <div className="fg">
                <div className="fgi"><label className="req">Nome Completo</label><input placeholder="Ex: Ana Oliveira" value={newUser.nome} onChange={e=>setNewUser({...newUser,nome:e.target.value})}/></div>
                <div className="fgi"><label className="req">E-mail</label><input type="email" placeholder="usuario@municipio.gov.br" value={newUser.email} onChange={e=>setNewUser({...newUser,email:e.target.value})}/></div>
                <div className="fgi"><label>Cargo</label><input placeholder="Ex: Analista Tributário" value={newUser.cargo} onChange={e=>setNewUser({...newUser,cargo:e.target.value})}/></div>
                <div className="fgi"><label>Perfil</label>
                  <select value={newUser.perfil} onChange={e=>setNewUser({...newUser,perfil:e.target.value})}>
                    <option value="operacional">👤 Operacional</option>
                    <option value="admin">🔑 Administrador</option>
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button className="btn btn-ghost" onClick={()=>setShowAddUser(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={salvarUsuario}>✓ Salvar Usuário</button>
              </div>
            </div>
          )}
        </div>
      )}

      {sub==="permissoes"&&(
        <div className="table-card">
          <div className="table-header"><div className="table-title">🔐 Matriz de Permissões</div></div>
          <div className="tw-wrap"><table>
            <thead><tr><th>Funcionalidade</th><th>Admin (Nível 1)</th><th>Operacional (Nível 2)</th></tr></thead>
            <tbody>
              {[["Todos os módulos",true,false],["Cadastrar empresas VAF",true,true],["Importar dados SEF",true,true],["Lançar contratos IBS",true,true],["Lançar pagamentos",true,true],["Iniciar contestação",true,true],["Excluir dados críticos",true,false],["Alterar configurações",true,false],["Relatórios estratégicos",true,false],["Gestão de usuários",true,false],["Exportar dados",true,true]].map(([feat,a,o])=>(
                <tr key={feat}><td>{feat}</td>
                  <td style={{color:a?"var(--green)":"var(--red)",fontWeight:600}}>{a?"✓ Permitido":"✗ Negado"}</td>
                  <td style={{color:o?"var(--green)":"var(--red)",fontWeight:600}}>{o?"✓ Permitido":"✗ Negado"}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {sub==="pwa"&&(
        <div className="form-card">
          <div className="form-title">📱 Configuração PWA – Instalar no Celular</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            <div>
              <div style={{fontSize:12.5,fontWeight:700,marginBottom:8,color:"var(--navy)"}}>public/manifest.json</div>
              <pre style={{background:"var(--navy)",color:"#00C2D4",padding:14,borderRadius:9,fontSize:11,fontFamily:"IBM Plex Mono,monospace",overflow:"auto",lineHeight:1.7,whiteSpace:"pre-wrap"}}>{`{
  "name": "Prefeitura Resolve",
  "short_name": "Pref. Resolve",
  "start_url": "/",
  "display": "standalone",
  "background_color": "#0B1F3A",
  "theme_color": "#0B1F3A",
  "icons": [
    {
      "src": "/icons/icon-192.png",
      "sizes": "192x192"
    },
    {
      "src": "/icons/icon-512.png",
      "sizes": "512x512"
    }
  ]
}`}</pre>
            </div>
            <div>
              <div style={{fontSize:12.5,fontWeight:700,marginBottom:8,color:"var(--navy)"}}>public/sw.js (Cache Offline)</div>
              <pre style={{background:"var(--navy)",color:"#2DC653",padding:14,borderRadius:9,fontSize:11,fontFamily:"IBM Plex Mono,monospace",overflow:"auto",lineHeight:1.7,whiteSpace:"pre-wrap"}}>{`const CACHE = 'pr-v1';
const URLS = ['/','/index.html'];

self.addEventListener('install', e => {
  e.waitUntil(
    caches.open(CACHE)
      .then(c => c.addAll(URLS))
  );
});

self.addEventListener('fetch', e => {
  e.respondWith(
    fetch(e.request).catch(() =>
      caches.match(e.request)
    )
  );
});`}</pre>
            </div>
          </div>
          <div style={{marginTop:14,padding:"12px 14px",background:"rgba(26,111,196,0.06)",borderRadius:9,fontSize:12.5}}>
            <strong>Passos para deploy:</strong> 1) <code style={{fontFamily:"IBM Plex Mono",background:"rgba(0,0,0,0.08)",padding:"1px 6px",borderRadius:4}}>npx create-react-app prefeitura-resolve</code> → 2) Cole o componente como <code style={{fontFamily:"IBM Plex Mono",background:"rgba(0,0,0,0.08)",padding:"1px 6px",borderRadius:4}}>App.jsx</code> → 3) Configure Firebase → 4) <code style={{fontFamily:"IBM Plex Mono",background:"rgba(0,0,0,0.08)",padding:"1px 6px",borderRadius:4}}>firebase deploy</code> → botão "Adicionar à tela inicial" aparece automaticamente no Chrome Mobile.
          </div>
        </div>
      )}
    </div>
  );
}

// ── ROOT ─────────────────────────────────────────────────────────────────────
export default function App(){
  const { user, loading: authLoading, login, logout } = useAuth();
  const [page,setPage]=useState("dashboard");
  const [empresas,setEmpresas]=useState(EMPRESAS);
  const [contratos,setContratos]=useState(CONTRATOS);
  const [toast,setToast]=useState(null);
  const showToast=(msg,type="success")=>setToast({msg,type});

  useEffect(()=>{
    const s=document.createElement("style");
    s.textContent=css;
    document.head.appendChild(s);
    return()=>document.head.removeChild(s);
  },[]);

  useEffect(()=>{
    if(!user) return;
    Promise.all([getEmpresas(), getContratos()])
      .then(([emp, con])=>{ setEmpresas(emp.length?emp:EMPRESAS); setContratos(con.length?con:CONTRATOS); })
      .catch(()=>{ /* fallback to mock data on error */ });
  },[user]);

  if(authLoading) return <><style>{css}</style><div style={{height:"100vh",background:"var(--navy)",display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:14}}>Carregando...</div></>;
  if(!user) return <><style>{css}</style><Login login={login} showToast={showToast}/></>;

  const nav=[
    {id:"dashboard",icon:"⬛",label:"Dashboard Geral",sec:"Principal"},
    {id:"mapa",icon:"🗺",label:"Mapa Territorial",sec:"Principal"},
    {div:true},
    {id:"vaf",icon:"📉",label:"VAF – ICMS",sec:"VAF",badge:"3"},
    {id:"ibs",icon:"🔮",label:"IBS – Contratos",sec:"IBS"},
    {div:true},
    {id:"relatorios",icon:"📑",label:"Relatórios",sec:"Dados"},
    {id:"importacao",icon:"🔄",label:"Importar / Exportar",sec:"Dados"},
    {div:true},
    {id:"admin",icon:"⚙️",label:"Administração",sec:"Sistema"},
  ];

  const renderPage=()=>{
    switch(page){
      case "dashboard": return <Dashboard setPage={setPage} empresas={empresas} contratos={contratos}/>;
      case "vaf": return <Vaf empresas={empresas} setEmpresas={setEmpresas} showToast={showToast}/>;
      case "ibs": return <Ibs contratos={contratos} setContratos={setContratos} showToast={showToast}/>;
      case "mapa": return <Mapa/>;
      case "relatorios": return <Relatorios empresas={empresas} contratos={contratos} showToast={showToast}/>;
      case "importacao": return <ImportacaoSEF showToast={showToast}/>;
      case "admin": return <Admin showToast={showToast}/>;
      default: return <Dashboard setPage={setPage} empresas={empresas} contratos={contratos}/>;
    }
  };

  let lastSec="";
  return(
    <div className="app">
      <style>{css}</style>
      <header className="header">
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div className="logo-mark">GV</div>
          <div><div className="logo-text">Gerenciador VAF IBS</div><div className="logo-sub">Gestão VAF · IBS · ICMS</div></div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div className="alert-pill" onClick={()=>setPage("vaf")}>⚠ 3 alertas críticos VAF</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{textAlign:"right"}}><div style={{fontSize:12.5,fontWeight:600,color:"white"}}>Município de Exemplo – MG</div><div style={{fontSize:11,color:"rgba(255,255,255,0.45)"}}>Ano Base: 2024</div></div>
          <div style={{width:34,height:34,background:"linear-gradient(135deg,#2484E4,#1A3560)",borderRadius:"50%",border:"2px solid rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"white",fontWeight:700}}>MG</div>
          <div className="user-chip">
            <div className="user-avatar">{user.initials}</div>
            <div style={{display:"flex",flexDirection:"column"}}><div style={{fontSize:12,color:"rgba(255,255,255,0.85)",fontWeight:500}}>{user.nome}</div><div style={{fontSize:10,color:"rgba(255,255,255,0.4)"}}>{user.role==="admin"?"Administrador":"Operacional"}</div></div>
          </div>
          <button style={{fontSize:11,color:"rgba(255,255,255,0.4)",padding:"5px 9px",border:"1px solid rgba(255,255,255,0.12)",borderRadius:6,cursor:"pointer",background:"none",fontFamily:"'Sora',sans-serif",transition:"all 0.2s"}} onClick={logout} onMouseOver={e=>{e.target.style.color="white";e.target.style.borderColor="rgba(255,255,255,0.3)"}} onMouseOut={e=>{e.target.style.color="rgba(255,255,255,0.4)";e.target.style.borderColor="rgba(255,255,255,0.12)"}}>Sair</button>
        </div>
      </header>

      <div className="app-body">
        <nav className="sidebar">
          {nav.map((item,i)=>{
            if(item.div) return <div key={i} className="nav-div"/>;
            const showSec=item.sec&&item.sec!==lastSec;
            if(showSec) lastSec=item.sec;
            return(
              <div key={item.id}>
                {showSec&&<div className="nav-sec-label">{item.sec}</div>}
                <div className={`nav-item ${page===item.id?"active":""}`} onClick={()=>setPage(item.id)}>
                  <span style={{fontSize:15,width:18,textAlign:"center",flexShrink:0}}>{item.icon}</span>
                  {item.label}
                  {item.badge&&<span className="nav-badge">{item.badge}</span>}
                </div>
              </div>
            );
          })}
        </nav>
        <main className="main">{renderPage()}</main>
      </div>
      {toast&&<Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );
}
